import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  leaveShare, listLinks, listSharedFolders, revokeLink, unshareFolder,
} from '../api/share.ts';
import { useAuth } from '../store/auth.ts';
import { formatBytes, formatDate } from '../lib/format.ts';
import { toast } from '../store/toast.ts';
import { EmptyState } from '../components/common/EmptyState.tsx';
import { ConfirmDialog } from '../components/common/ConfirmDialog.tsx';
import { MembersDialog } from '../components/files/MembersDialog.tsx';
import { FileIcon } from '../components/files/FileIcon.tsx';
import { IconCopy, IconShare, IconTrash } from '../lib/icons.tsx';
import type { SharedFolder } from '../api/share.ts';

const ROLE_LABEL: Record<string, string> = {
  owner: '소유자', editor: '편집 가능', viewer: '보기 전용',
};

export function SharedPage() {
  const queryClient = useQueryClient();
  const bootstrap = useAuth((s) => s.bootstrap);
  const [members, setMembers] = useState<SharedFolder | null>(null);
  const [pending, setPending] = useState<{ kind: 'unshare' | 'leave'; folder: SharedFolder } | null>(null);

  const folders = useQuery({ queryKey: ['shared-folders'], queryFn: listSharedFolders });
  const links = useQuery({ queryKey: ['share-links'], queryFn: listLinks });

  const refresh = (): void => {
    void bootstrap();
    void queryClient.invalidateQueries({ queryKey: ['shared-folders'] });
  };

  const unshare = useMutation({
    mutationFn: (nsId: string) => unshareFolder(nsId),
    onSuccess: (r) => {
      toast.success(`공유를 해제했습니다. 내 드라이브 ${r.path} 로 돌아왔습니다.`);
      setPending(null); refresh();
    },
    onError: (e: Error) => { toast.error(e); setPending(null); },
  });

  const leave = useMutation({
    mutationFn: (nsId: string) => leaveShare(nsId),
    onSuccess: () => { toast.info('공유 폴더에서 나왔습니다'); setPending(null); refresh(); },
    onError: (e: Error) => { toast.error(e); setPending(null); },
  });

  const revoke = useMutation({
    mutationFn: revokeLink,
    onSuccess: () => {
      toast.info('링크를 폐기했습니다');
      void queryClient.invalidateQueries({ queryKey: ['share-links'] });
    },
    onError: (e: Error) => { toast.error(e); },
  });

  const folderItems = folders.data?.items ?? [];
  const linkItems = links.data?.items ?? [];

  return (
    <div className="page">
      <div className="page__head">
        <h1 className="page__title">공유</h1>
        <span className="muted">폴더 {folderItems.length} · 링크 {linkItems.length}</span>
      </div>

      <div className="page__body">
        <div className="sidebar__section" style={{ paddingLeft: 16 }}>공유 폴더</div>
        {folderItems.length === 0 && (
          <EmptyState
            icon={<IconShare size={36} className="faint" />}
            title="공유 중인 폴더가 없습니다"
            description="파일 목록에서 폴더를 우클릭해 '폴더 공유하기'를 선택하세요."
          />
        )}
        {folderItems.map((folder) => (
          <div key={folder.namespace_id} className="frow"
            style={{ gridTemplateColumns: '32px 1fr 160px 130px 190px' }}>
            <FileIcon node={{ type: 'folder', name: folder.name, mime: null }} />
            <div className="frow__name">
              <Link to={`/ns/${folder.namespace_id}`} className="frow__label">{folder.name}</Link>
              <span className="frow__badge">{ROLE_LABEL[folder.role]}</span>
            </div>
            <div className="frow__meta truncate">
              {folder.is_owner ? '내가 공유함' : `${folder.owner.name} 님이 공유`}
            </div>
            <div className="frow__meta">
              {folder.file_count}개 · {formatBytes(folder.total_size)}
            </div>
            <div className="row">
              <button className="btn btn--sm" onClick={() => { setMembers(folder); }}>
                참여자 {folder.member_count}
              </button>
              {folder.is_owner ? (
                <button className="btn btn--sm"
                  onClick={() => { setPending({ kind: 'unshare', folder }); }}>
                  공유 해제
                </button>
              ) : (
                <button className="btn btn--sm"
                  onClick={() => { setPending({ kind: 'leave', folder }); }}>
                  나가기
                </button>
              )}
            </div>
          </div>
        ))}

        <div className="sidebar__section" style={{ paddingLeft: 16 }}>공개 링크</div>
        {linkItems.length === 0 && <p className="muted" style={{ padding: '0 16px' }}>없음</p>}
        {linkItems.map((link) => (
          <div key={link.id} className="frow" style={{ gridTemplateColumns: '32px 1fr 1fr 150px' }}>
            <FileIcon node={{ type: link.type, name: link.name, mime: null }} />
            <div className="frow__name">
              <span className="frow__label" title={link.path}>{link.name}</span>
              {link.has_password && <span className="frow__badge">비밀번호</span>}
            </div>
            <div className="frow__meta truncate mono">{link.url}</div>
            <div className="row">
              <span className="frow__meta spacer">
                조회 {link.view_count} · 받음 {link.download_count}
                {link.expires_at && (
                  <div className="faint" style={{ fontSize: 11 }}>
                    {formatDate(link.expires_at)}까지
                  </div>
                )}
              </span>
              <button className="btn btn--sm btn--icon" aria-label="링크 복사"
                onClick={() => {
                  void navigator.clipboard?.writeText(link.url);
                  toast.success('복사했습니다');
                }}>
                <IconCopy size={14} />
              </button>
              <button className="btn btn--sm btn--icon" aria-label="폐기"
                onClick={() => { revoke.mutate(link.id); }}>
                <IconTrash size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {members && (
        <MembersDialog folder={members} onClose={() => { setMembers(null); }} />
      )}

      {pending?.kind === 'unshare' && (
        <ConfirmDialog
          title="공유 해제"
          message={`'${pending.folder.name}' 공유를 해제합니다.\n참여자는 더 이상 접근할 수 없고, 폴더는 내 드라이브 최상위로 돌아옵니다.`}
          confirmLabel="공유 해제" danger busy={unshare.isPending}
          onConfirm={() => { unshare.mutate(pending.folder.namespace_id); }}
          onCancel={() => { setPending(null); }}
        />
      )}
      {pending?.kind === 'leave' && (
        <ConfirmDialog
          title="공유 폴더 나가기"
          message={`'${pending.folder.name}' 에서 나갑니다.\n다시 초대받기 전까지 접근할 수 없습니다.`}
          confirmLabel="나가기" danger busy={leave.isPending}
          onConfirm={() => { leave.mutate(pending.folder.namespace_id); }}
          onCancel={() => { setPending(null); }}
        />
      )}
    </div>
  );
}
